<template>
  <v-card class="soft-card pa-4">
    <div class="section-heading">Payouts</div>
    <div class="muted body-2 mt-1">Double-click a payout to view its transactions.</div>

    <v-progress-linear v-if="loading" indeterminate color="green" height="3" class="mt-2"></v-progress-linear>

    <div class="payout-list mt-3">
      <v-card
        flat
        class="panel-muted pa-3 mb-3 payout-card"
        v-for="(item, i) in payouts"
        :key="i"
        @dblclick.native="openDialog(item)"
      >
        <div class="bold-dark mb-2" v-if="item.syndicate_name">{{ item.syndicate_name }}</div>
        <v-layout row justify-space-between>
          <div class="caption muted">
            <div>Gross Amount:</div>
            <div>Management Fee:</div>
            <div>Net Pay:</div>
            <div>Pay Date:</div>
          </div>
          <div style="text-align: right;">
            <div>{{ item.sum_gross_amt | currency }}</div>
            <div>{{ item.sum_mgt_fee | currency }}</div>
            <div class="green-text-custom" style="font-weight: bold;">{{ item.sum_net_pay | currency }}</div>
            <div>{{ item.pay_date }}</div>
          </div>
        </v-layout>
        <v-divider class="my-2"></v-divider>
        <div style="text-align: center;">
          <v-btn flat small class="text-none ma-0" @click.stop="exportPayoutSheet(item)">
            <v-icon small left>vertical_align_bottom</v-icon>Download
          </v-btn>
        </div>
      </v-card>

      <div v-if="!loading && payouts.length === 0" class="muted body-2 text-center pa-4">No payouts found.</div>

      <v-btn
        v-if="page < lastPage"
        block
        outline
        color="blue-grey"
        class="export-btn"
        :loading="loading"
        @click="loadMore"
      >Load more</v-btn>
    </div>

    <v-dialog v-model="dialog" max-width="1100" scrollable>
      <v-card>
        <v-card-title class="headline">
          Payouts
          <span class="muted body-2 ml-2" v-if="selectedItem.pay_date">({{ selectedItem.pay_date }})</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0" style="max-height: 70vh;">
          <v-progress-linear v-if="loadingTable" indeterminate color="green" height="3"></v-progress-linear>
          <v-data-table
            class="report-table"
            :headers="txHeaders"
            :items="transactions"
            hide-actions
          >
            <template slot="items" slot-scope="props">
              <td style="text-align: left;">{{ props.item.contract_MID }}</td>
              <td style="text-align: left;">{{ props.item.syndication_company_name }}</td>
              <td style="text-align: left;">{{ props.item.total_gross_amount | currency }}</td>
              <td style="text-align: left;">{{ props.item.total_mgt_fee | currency }}</td>
              <td style="text-align: left;">{{ props.item.total_net_pay | currency }}</td>
              <td style="text-align: left;">{{ props.item.numbers_of_transactions }}</td>
            </template>
          </v-data-table>
          <div v-if="!loadingTable && transactions.length === 0" class="muted body-2 text-center pa-4">No transactions found.</div>
          <div class="text-center pa-2" v-if="transactions.length < txTotal">
            <v-btn small outline color="blue-grey" :loading="loadingTable" @click="loadMoreTransactions">Load more</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from 'axios'
import { URL } from '../../../env'

export default {
  props: {
    syndicationId: { default: null },
  },
  data() {
    return {
      syndication_id: this.syndicationId || localStorage.getItem('syndication_id'),
      user_id: localStorage.getItem('user_id'),
      payouts: [],
      page: 1,
      lastPage: 1,
      loading: false,
      dialog: false,
      selectedItem: {},
      transactions: [],
      txPage: 1,
      txTotal: 0,
      loadingTable: false,
      txHeaders: [
        { text: 'MID', align: 'left', value: 'contract_MID', sortable: false },
        { text: 'Company Name', align: 'left', value: 'syndication_company_name', sortable: false },
        { text: 'Gross Amount', align: 'left', value: 'total_gross_amount', sortable: false },
        { text: 'Mgt Fee', align: 'left', value: 'total_mgt_fee', sortable: false },
        { text: 'Net Pay', align: 'left', value: 'total_net_pay', sortable: false },
        { text: 'No. of Transactions', align: 'left', value: 'numbers_of_transactions', sortable: false },
      ],
    };
  },
  mounted() {
    this.getPaydates();
  },
  watch: {
    syndicationId: function (id) {
      this.syndication_id = id;
      this.page = 1;
      this.dialog = false;
      this.getPaydates();
    },
  },
  methods: {
    getPaydates: function () {
      if (!this.syndication_id) return;
      this.loading = true;
      axios.get(`${URL}/api/payouts/get_payouts_paydates/${this.syndication_id}`, {
        params: { page: this.page, length: 25, user_id: this.user_id }
      }).then(res => {
        const rows = (res.data && res.data.data) || [];
        this.payouts = this.page === 1 ? rows : this.payouts.concat(rows);
        this.lastPage = (res.data && res.data.last_page) || 1;
        this.loading = false;
      }).catch(() => { this.loading = false; });
    },
    loadMore: function () {
      if (this.page < this.lastPage) {
        this.page += 1;
        this.getPaydates();
      }
    },
    openDialog: function (item) {
      this.selectedItem = item;
      this.dialog = true;
      this.txPage = 1;
      this.transactions = [];
      this.txTotal = 0;
      this.getTransactions();
    },
    getTransactions: function () {
      if (!this.selectedItem || !this.selectedItem.pay_date) return;
      this.loadingTable = true;
      axios.get(`${URL}/api/payouts/get_payouts_wrt_paydate`, {
        params: {
          // Each payout row belongs to a specific child syndicate, so show that
          // syndicate's transactions for the pay date.
          syndication_id: this.selectedItem.syndication_id,
          pay_date: this.selectedItem.pay_date,
          page: this.txPage,
          length: 25
        }
      }).then(res => {
        const rows = (res.data && res.data.data) || [];
        this.transactions = this.txPage === 1 ? rows : this.transactions.concat(rows);
        this.txTotal = (res.data && res.data.total) || 0;
        this.loadingTable = false;
      }).catch(() => { this.loadingTable = false; });
    },
    loadMoreTransactions: function () {
      if (this.transactions.length < this.txTotal) {
        this.txPage += 1;
        this.getTransactions();
      }
    },
    exportPayoutSheet: function (item) {
      axios({
        url: `${URL}/api/payouts/export_syndicated_payouts`,
        method: 'POST',
        responseType: 'blob',
        data: { syndication_id: item.syndication_id, pay_date: item.pay_date }
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'payout_(' + item.pay_date + ').xlsx');
        document.body.appendChild(link);
        link.click();
      });
    },
  },
};
</script>

<style scoped>
.payout-list {
  max-height: 520px;
  overflow-y: auto;
}
.payout-card {
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}
.payout-card:hover {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}
</style>

<style scoped src="./Styles/SuperSyndicate.css"></style>
